import { useState, useEffect, useMemo } from 'react';
import { BursaryRequest } from '../../types/organization/finances';
import { fetchBursaryRequests, approveBursaryRequest, rejectBursaryRequest } from '../../services/organization/finances';

export const useBursaryRequests = (organizationUuid: string | null) => {
    const [bursaryRequests, setBursaryRequests] = useState<BursaryRequest[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedBursary, setSelectedBursary] = useState<BursaryRequest | null>(null);
    const [showBursaryModal, setShowBursaryModal] = useState<boolean>(false);
    const [showName, setShowName] = useState<{ [key: string]: boolean }>({});
    const [showModalName, setShowModalName] = useState<{ [key: string]: boolean }>({});
    const [showModalEmail, setShowModalEmail] = useState<{ [key: string]: boolean }>({});
    const [statusFilters, setStatusFilters] = useState<{ [key: string]: boolean }>({
        pending: true,
        approved: true,
        rejected: true
    });
    const [selectedBursaryEvent, setSelectedBursaryEvent] = useState<string>('all');
    const [showBursaryEventDropdown, setShowBursaryEventDropdown] = useState<boolean>(false);

    useEffect(() => {
        const loadBursaryRequests = async () => {
            if (!organizationUuid) return;
            
            setIsLoading(true);
            setError(null);
            
            try {
                const data = await fetchBursaryRequests(organizationUuid);
                setBursaryRequests(data);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred while fetching bursary requests');
                console.error('Error fetching bursary requests:', err);
            } finally {
                setIsLoading(false);
            }
        };

        loadBursaryRequests();
    }, [organizationUuid]);

    const handleAccept = async (requestUuid: string): Promise<void> => {
        try {
            const bursaryRequest = bursaryRequests.find(req => req.uuid === requestUuid);
            if (!bursaryRequest) {
                throw new Error('Bursary request not found');
            }

            await approveBursaryRequest(requestUuid, bursaryRequest.transaction_uuid, bursaryRequest.amount);

            setBursaryRequests(prevRequests => 
                prevRequests.map(req => 
                    req.uuid === requestUuid 
                        ? { ...req, status: 'approved' }
                        : req
                )
            );

        } catch (error) {
            console.error('Error processing bursary approval:', error);
            setError(error instanceof Error ? error.message : 'Failed to process bursary approval');
        }
    };

    const handleReject = async (requestUuid: string): Promise<void> => { 
        try {
            await rejectBursaryRequest(requestUuid);

            setBursaryRequests(prevRequests => 
                prevRequests.map(req => 
                    req.uuid === requestUuid 
                        ? { ...req, status: 'rejected' }
                        : req
                )
            );

        } catch (error) {
            console.error('Error rejecting bursary request:', error);
            setError(error instanceof Error ? error.message : 'Failed to reject bursary request');
        }
    };

    const toggleNameVisibility = (uuid: string) => {
        setShowName(prev => ({
            ...prev,
            [uuid]: !prev[uuid]
        }));
    };

    const toggleModalNameVisibility = (uuid: string) => {
        setShowModalName(prev => ({
            ...prev,
            [uuid]: !prev[uuid]
        }));
    };

    const toggleModalEmailVisibility = (uuid: string) => {
        setShowModalEmail(prev => ({
            ...prev,
            [uuid]: !prev[uuid]
        }));
    };

    const handleViewDetails = (bursary: BursaryRequest) => {
        setSelectedBursary(bursary);
        setShowBursaryModal(true);
    };

    const toggleStatusFilter = (status: string) => {
        setStatusFilters(prev => ({
            ...prev,
            [status]: !prev[status]
        }));
    };

    // Get unique events that have bursary requests
    const uniqueEvents = useMemo(() => {
        const events = new Map<string, string>();
        events.set('all', 'All Events');
        bursaryRequests.forEach(request => {
            if (request.event?.uuid) {
                events.set(request.event.uuid, request.event.title);
            }
        });
        return Array.from(events.entries());
    }, [bursaryRequests]);

    const filteredBursaryRequests = useMemo(() => 
        bursaryRequests.filter(request => 
            (selectedBursaryEvent === 'all' || request.event?.uuid === selectedBursaryEvent) &&
            statusFilters[request.status]
        ),
        [bursaryRequests, selectedBursaryEvent, statusFilters]
    );

    return {
        bursaryRequests,
        isLoading,
        error,
        selectedBursary,
        showBursaryModal,
        showName,
        showModalName,
        showModalEmail,
        statusFilters,
        selectedBursaryEvent,
        showBursaryEventDropdown,
        filteredBursaryRequests,
        uniqueEvents,
        setShowBursaryModal,
        setSelectedBursaryEvent,
        setShowBursaryEventDropdown,
        handleAccept,
        handleReject,
        toggleNameVisibility,
        toggleModalNameVisibility,
        toggleModalEmailVisibility,
        handleViewDetails,
        toggleStatusFilter,
        setError
    };
}; 